import React, { useState, useRef } from 'react';
import { SequenceEvent, TrackSequence } from './TimelineSequencer';

interface TimelineSequencerUIProps {
  sequence: TrackSequence | null;
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  onSeek: (time: number) => void;
  onAddEvent: (timestamp: number) => void;
  onEditEvent: (event: SequenceEvent) => void;
  onDeleteEvent: (eventId: string) => void;
  onExport?: () => void;
  onImport?: (file: File) => void;
  onClose?: () => void;
  className?: string;
}

const TimelineSequencerUI: React.FC<TimelineSequencerUIProps> = ({
  sequence,
  currentTime,
  duration,
  isPlaying,
  onSeek,
  onAddEvent,
  onEditEvent,
  onDeleteEvent,
  onExport,
  onImport,
  onClose,
  className = ''
}) => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // const [isDragging, setIsDragging] = useState(false); // Unused - for future drag features
  const [hoveredTime, setHoveredTime] = useState<number | null>(null);

  // Convert time to pixel position (unused - for future features)
  // const timeToPixel = (time: number, width: number) => {
  //   return (time / duration) * width;
  // };

  // Convert pixel position to time
  const pixelToTime = (pixel: number, width: number) => {
    return (pixel / width) * duration;
  };

  // Format time for display (MM:SS)
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle timeline click
  const handleTimelineClick = (e: React.MouseEvent) => {
    if (!timelineRef.current) return;
    
    const rect = timelineRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickTime = pixelToTime(clickX, rect.width);
    
    if (e.shiftKey || e.ctrlKey) {
      // Add new event
      onAddEvent(clickTime);
    } else {
      // Seek to time
      onSeek(clickTime);
    }
  };

  // Handle mouse move for hover time display
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!timelineRef.current) return;
    
    const rect = timelineRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const hoverTime = pixelToTime(mouseX, rect.width);
    setHoveredTime(hoverTime);
  };

  const handleMouseLeave = () => {
    setHoveredTime(null);
  };

  // Handle file import
  const handleFileImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onImport) {
      onImport(file);
      // Reset the input so the same file can be imported again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Generate time markers
  const generateTimeMarkers = () => {
    const markers = [];
    const interval = duration > 300 ? 60 : 30; // 1 min or 30 sec intervals
    
    for (let time = 0; time <= duration; time += interval) {
      markers.push(time);
    }
    
    return markers;
  };

  const events = sequence?.events || [];
  const timeMarkers = generateTimeMarkers();

  return (
    <div className={`timeline-sequencer ${className}`}>
      {/* Timeline Header */}
      <div className="flex justify-between items-center mb-2 px-2">
        <div className="flex items-center gap-4">
          <div className="text-white text-sm font-['IBM_Plex_Mono:Regular',_sans-serif]">
            TIMELINE: {sequence?.trackTitle || 'No Track'}
          </div>
          
          {/* Export/Import Buttons */}
          <div className="flex gap-2">
            {onExport && (
              <button
                onClick={onExport}
                className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors"
                title="Export Timeline"
              >
                Export
              </button>
            )}
            {onImport && (
              <>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition-colors"
                  title="Import Timeline"
                >
                  Import
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleFileImport}
                  className="hidden"
                />
              </>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="text-white text-sm font-['IBM_Plex_Mono:Regular',_sans-serif]">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
          
          {/* Close Button */}
          {onClose && (
            <button
              onClick={onClose}
              className="w-6 h-6 flex items-center justify-center text-white hover:text-gray-300 transition-colors"
              title="Close Timeline"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 4L4 12M4 4L12 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Timeline Container */}
      <div className="relative bg-gray-900 rounded-lg overflow-hidden">
        {/* Time Markers */}
        <div className="flex justify-between py-2 px-2 bg-gray-800 text-xs text-gray-400">
          {timeMarkers.map((time, index) => (
            <div key={index} className="text-center">
              {formatTime(time)}
            </div>
          ))}
        </div>

        {/* Main Timeline */}
        <div
          ref={timelineRef}
          className="relative h-20 bg-gray-700 cursor-pointer select-none"
          onClick={handleTimelineClick}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          {/* Time Grid Lines */}
          <div className="absolute inset-0 pointer-events-none">
            {timeMarkers.map((time, index) => (
              <div
                key={index}
                className="absolute top-0 bottom-0 w-px bg-gray-600"
                style={{ left: `${(time / duration) * 100}%` }}
              />
            ))}
          </div>

          {/* Events */}
          {events.map((event) => (
            <div
              key={event.id}
              className="absolute top-2 bottom-2 min-w-[4px] bg-blue-500 rounded cursor-pointer hover:bg-blue-400 transition-colors group"
              style={{ 
                left: `${(event.timestamp / duration) * 100}%`,
                width: event.duration ? `${(event.duration / duration) * 100}%` : '4px'
              }}
              onClick={(e) => {
                e.stopPropagation();
                onEditEvent(event);
              }}
              title={`${event.title} - ${formatTime(event.timestamp)}`}
            >
              {/* Event Label */}
              <div className="absolute -top-6 left-0 text-xs text-white bg-gray-900 px-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                {event.title}
              </div>
              
              {/* Delete Button */}
              <button
                className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteEvent(event.id);
                }}
              >
                ×
              </button>
            </div>
          ))}

          {/* Current Time Indicator */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg z-20"
            style={{ left: `${(currentTime / duration) * 100}%` }}
          >
            <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-white" />
          </div>

          {/* Hover Time Indicator */}
          {hoveredTime !== null && (
            <div
              className="absolute top-0 bottom-0 w-px bg-yellow-400 opacity-50 pointer-events-none"
              style={{ left: `${(hoveredTime / duration) * 100}%` }}
            >
              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-yellow-400 text-black px-2 py-1 rounded text-xs whitespace-nowrap">
                {formatTime(hoveredTime)}
              </div>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="p-2 bg-gray-800 text-xs text-gray-400 font-['IBM_Plex_Mono:Regular',_sans-serif]">
          Click to seek • Shift+Click to add event • Hover events to edit/delete
        </div>
      </div>
    </div>
  );
};

export default TimelineSequencerUI;